import { DefaultTheme } from "styled-components";

export const theme: DefaultTheme = {
  colour: {
    // ===== COLORES DE MARCA =====
    // Verde principal de la marca (Dry Stone Finder)
    brand: {
      primary: "#2d5016", // Verde oscuro principal
      secondary: "#4a7c59", // Verde medio secundario
      accent: "#6b8e23", // Verde claro de acento
      light: "#8fbc8f", // Verde muy claro
      dark: "#1a3d0f", // Verde muy oscuro
    },

    // ===== COLORES SEMÁNTICOS =====
    semantic: {
      success: "#28a745", // Verde de éxito
      warning: "#ffc107", // Amarillo de advertencia
      error: "#dc3545", // Rojo de error
      info: "#17a2b8", // Azul de información
    },

    // ===== COLORES DE TEXTO =====
    text: {
      primary: "#1a1a1a", // Texto principal (muy oscuro)
      secondary: "#4a4a4a", // Texto secundario
      tertiary: "#6b6b6b", // Texto terciario
      muted: "#8a8a8a", // Texto atenuado
      inverse: "#ffffff", // Texto sobre fondos oscuros
      disabled: "#b0b0b0", // Texto deshabilitado
    },

    // ===== COLORES DE FONDO =====
    background: {
      primary: "#ffffff", // Fondo principal
      secondary: "#f8f9fa", // Fondo secundario
      tertiary: "#f1f3f4", // Fondo terciario
      dark: "#1a1a1a", // Fondo oscuro
      overlay: "rgba(0, 0, 0, 0.5)", // Overlay para modales
    },

    // ===== COLORES DE BORDES =====
    border: {
      light: "#e1e5e9", // Borde claro
      medium: "#c1c7cd", // Borde medio
      dark: "#8a8a8a", // Borde oscuro
      focus: "#4a7c59", // Borde de focus
    },

    // ===== COLORES DE INTERACCIÓN =====
    interactive: {
      primary: "#2d5016", // Botón primario
      secondary: "#4a7c59", // Botón secundario
      hover: "#6b8e23", // Estado hover
      active: "#1a3d0f", // Estado activo
      disabled: "#e1e5e9", // Estado deshabilitado
    },

    // ===== COLORES DE ESTADO =====
    state: {
      loading: "#f0f0f0", // Estado de carga
      selected: "#e8f5e8", // Estado seleccionado
      highlighted: "#fff3cd", // Estado destacado
    },

    // ===== COLORES LEGACY (para compatibilidad) =====
    // Estos se mantienen para no romper código existente
    main: "#2d5016", // Alias para brand.primary
    secondary: "#1a1a1a", // Alias para background.dark
    primary: "#4a7c59", // Alias para brand.secondary
    accent: "#6b8e23", // Alias para brand.accent
    white: "#ffffff", // Alias para background.primary
    brokenWhite: "#f8f9fa", // Alias para background.secondary
    lightGray: "#f1f3f4", // Alias para background.tertiary
    darkGray: "#6b6b6b", // Alias para text.tertiary
    error: "#dc3545", // Alias para semantic.error
    success: "#28a745", // Alias para semantic.success
    warning: "#ffc107", // Alias para semantic.warning
    textPrimary: "#1a1a1a", // Alias para text.primary
    textSecondary: "#4a4a4a", // Alias para text.secondary
    textMuted: "#8a8a8a", // Alias para text.muted
    borderLight: "#e1e5e9", // Alias para border.light
    borderMedium: "#c1c7cd", // Alias para border.medium
    backgroundLight: "#f8f9fa", // Alias para background.secondary
    backgroundMedium: "#f1f3f4", // Alias para background.tertiary
  },
  fonts: {
    title: "Roboto",
    titleFontSizeS: "1.25rem",
    titleFontSizeM: "1.5rem",
    titleFontSizeL: "2rem",
    titleFontSizeXL: "2.5rem",
    text: "Roboto",
    textFontSizeS: "1rem",
    textFontSizeM: "1.25rem",
    textFontSizeL: "1.5rem",
    textFontSizeXL: "1.875rem",
    // Nuevas variables para mejor tipografía responsiva
    lineHeightTight: "1.2",
    lineHeightNormal: "1.5",
    lineHeightRelaxed: "1.7",
    fontWeightLight: "300",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "700",
  },
  border: {
    borderRadiusSmall: "10px",
    borderRadiusLarge: "15px",
    borderRadiusXL: "20px",
    // Nuevas variables para bordes
    borderWidthThin: "1px",
    borderWidthNormal: "2px",
    borderWidthThick: "3px",
  },
  padding: {
    mainMobilePadding: "10px",
    mainDesktopPadding: "20px",
    secondaryMobilePadding: "20px",
    secondaryDesktopPading: "40px",
  },
  gap: {
    mediumClickableGap: "20px",
    clickableGap: "40px",
  },
  // Nuevo sistema de breakpoints
  breakpoints: {
    mobile: "320px",
    tablet: "768px",
    desktop: "1024px",
    wide: "1200px",
    ultraWide: "1440px",
  },
  // Nuevo sistema de espaciado
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
    xxxl: "64px",
  },
  // Nuevo sistema de sombras
  shadows: {
    small: "0 2px 4px rgba(0, 0, 0, 0.1)",
    medium: "0 4px 8px rgba(0, 0, 0, 0.15)",
    large: "0 8px 16px rgba(0, 0, 0, 0.2)",
    // Nuevas sombras para mejor profundidad
    xsmall: "0 1px 2px rgba(0, 0, 0, 0.05)",
    xlarge: "0 12px 24px rgba(0, 0, 0, 0.25)",
  },
  // Nuevo sistema de transiciones
  transitions: {
    fast: "0.15s ease-in-out",
    normal: "0.3s ease-in-out",
    slow: "0.5s ease-in-out",
    // Nuevas transiciones para mejor UX
    bounce: "0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    smooth: "0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  // Nuevo sistema de z-index
  zIndex: {
    base: "1",
    dropdown: "1000",
    sticky: "1020",
    fixed: "1030",
    modalBackdrop: "1040",
    modal: "1050",
    popover: "1060",
    tooltip: "1070",
  },
};
